import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { isPurchaseProcessAlias } from '../../transactions/transaction';
import {
  PAYMENT_OPTION_DIRECT,
  PAYMENT_OPTION_SECURE,
  getPaymentOptions,
} from '../../util/paymentOptions';

import { Heading } from '../../components';

import css from './ListingPage.module.css';

// Payment options in the order they are shown, with the messages that explain them
const PAYMENT_OPTIONS = [
  {
    key: PAYMENT_OPTION_SECURE,
    titleId: 'ListingPage.paymentOptionSecureTitle',
    textId: 'ListingPage.paymentOptionSecureText',
  },
  {
    key: PAYMENT_OPTION_DIRECT,
    titleId: 'ListingPage.paymentOptionDirectTitle',
    textId: 'ListingPage.paymentOptionDirectText',
  },
];

/**
 * The SectionPaymentOptionsMaybe component.
 * Explains how the buyer can pay for the listing, based on the payment options that the seller
 * has selected. Listings without saved payment options are treated as secure payment listings.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Object} props.publicData - The publicData of the listing
 * @returns {JSX.Element} section for the payment options, or null if the listing is not a purchase listing
 */
const SectionPaymentOptionsMaybe = props => {
  const { className, rootClassName, publicData } = props;

  // Payment options are selected for purchase listings
  if (!isPurchaseProcessAlias(publicData?.transactionProcessAlias)) {
    return null;
  }

  const selectedOptions = getPaymentOptions(publicData);
  const options = PAYMENT_OPTIONS.filter(option => selectedOptions.includes(option.key));
  if (options.length === 0) {
    return null;
  }

  const classes = classNames(rootClassName || css.sectionPaymentOptions, className);

  return (
    <section className={classes} id="listing-payment-options">
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.paymentOptionsTitle" />
      </Heading>
      {options.map(({ key, titleId, textId }) => (
        <div key={key} className={css.paymentOption}>
          <Heading as="h3" rootClassName={css.paymentOptionTitle}>
            <FormattedMessage id={titleId} />
          </Heading>
          <p className={css.text}>
            <FormattedMessage id={textId} values={{ b: msgFragment => <b>{msgFragment}</b> }} />
          </p>
        </div>
      ))}
    </section>
  );
};

export default SectionPaymentOptionsMaybe;
